import React from 'react';

// Translations
import messages from './messages';
import { useIntl } from 'react-intl';

// Model
import { PlaceOfJurisdictionModel } from '../../models/PlaceOfJurisdictionModel';

// Resources
import { useDownloadPoaOrPPoa } from '../../services/resources/poaAndPpoa';
import { useCoBrandingParams } from '../../hooks/useCobrandingParams';

interface PermissionDescriptionProps {
  placeOfJurisdiction: PlaceOfJurisdictionModel | undefined;
  passengerId?: string;
  lettersToSign: LetterTypes[];
  productType?: string;
}

export type LetterTypes =
  | 'power-of-attorney'
  | 'partner-power-of-attorney'
  | 'court-power-of-attorney'
  | 'assignment-of-credit';

const typeToTranslationMap = {
  'power-of-attorney': 'poa',
  'partner-power-of-attorney': 'ppoa',
  'court-power-of-attorney': 'cpoa',
  'assignment-of-credit': 'aoc',
};

const PermissionDescription = ({
  placeOfJurisdiction,
  passengerId,
  lettersToSign,
  productType,
}: PermissionDescriptionProps): JSX.Element => {
  const { formatMessage } = useIntl();
  const { dynamicConfig } = useCoBrandingParams();
  const lawyerId = localStorage.getItem('yo:lawyerId');

  const linkProps = {
    className: 'link',
    target: '_blank',
    rel: 'noreferrer',
  };

  const handleClick = useDownloadPoaOrPPoa({
    passengerId,
    placeOfJurisdictionId: placeOfJurisdiction?.id,
  });

  const translations: any = {};

  lettersToSign?.forEach(value => {
    // eslint-disable-next-line react/no-multi-comp
    translations[typeToTranslationMap[value]] = (...chunks: string[]) => {
      return (
        // eslint-disable-next-line
        <a {...linkProps} onClick={() => handleClick(value)}>
          {chunks}
        </a>
      );
    };
  });

  const tacAndPoa =
    productType === 'french_court_delays'
      ? {
          // eslint-disable-next-line react/no-multi-comp
          tac: (...chunks: string[]) => (
            <a
              {...linkProps}
              href={`https://${dynamicConfig.portalUrl}/termsOfServices/download?filter[product_type]=${productType}`}
            >
              {chunks}
            </a>
          ),
          ...translations,
        }
      : lawyerId === '32'
      ? {
          // eslint-disable-next-line react/no-multi-comp
          tac: (...chunks: string[]) => (
            <a {...linkProps} href={dynamicConfig?.termsOfServiceUrl}>
              {chunks}
            </a>
          ),
          // eslint-disable-next-line react/no-multi-comp
          prices: (...chunks: string[]) => (
            <a {...linkProps} href={dynamicConfig?.pricingListUrl}>
              {chunks}
            </a>
          ),
          ...translations,
        }
      : {
          // eslint-disable-next-line react/no-multi-comp
          tac: (...chunks: string[]) => (
            <a {...linkProps} href={dynamicConfig?.termsOfServiceUrl}>
              {chunks}
            </a>
          ),
          // eslint-disable-next-line react/no-multi-comp
          prices: (...chunks: string[]) => (
            <a {...linkProps} href={dynamicConfig?.pricingListUrl}>
              {chunks}
            </a>
          ),
          ...translations,
        };

  const getPermissionMessage = () => {
    if (productType === 'french_court_delays') {
      return messages.permissionDescriptionFrenchCourt;
    }

    if (lettersToSign?.some(letter => letter === 'assignment-of-credit')) {
      return messages.permissionDescriptionLawyer;
    }

    return messages.permissionDescription;
  };

  const message = getPermissionMessage();

  return (
    <div className="mt-15 mb-15 sm:mt-10 sm:mb-0 text-sm sm:text-base text-left">
      {formatMessage(message, {
        poa: null,
        ppoa: null,
        cpoa: null,
        aoc: null,
        br: <br />,
        ...tacAndPoa,
      })}
    </div>
  );
};

export default PermissionDescription;
